/**
 * Resolve the chat thread that originated a patient's pending ingestion review.
 *
 * The pending-review badge is a count; clicking it must reopen the *originating*
 * chat thread (the one carrying the ingestion-summary message) in a new tab. The
 * backend links an ingestion run to a chat message via `ingestion_run_id`, so we
 * list the patient's threads and return the most recent one whose history
 * contains such a message.
 */
import apiUtils, { isApiError } from "../../apiUtils";
import logger from "../../utils/logger";
import type { ChatThread, ChatThreadDetail } from "../../types";

const THREADS_ENDPOINT = "/api/blueprint/threads";

/**
 * Find the thread id that carries a pending ingestion-review summary.
 *
 * Threads are listed most-recent-first; the first thread whose messages include
 * one with an `ingestion_run_id` is returned.
 *
 * @param patientId - The active patient id.
 * @returns The originating thread id, or null when none is found.
 */
export async function findPendingReviewThread(
  patientId: string,
): Promise<string | null> {
  const list = await apiUtils.fetchData<ChatThread[]>(
    `${THREADS_ENDPOINT}?patient_id=${encodeURIComponent(patientId)}`,
  );
  if (isApiError(list)) {
    logger.devOnly.warn("findPendingReviewThread: list failed");
    return null;
  }
  for (const thread of list) {
    const detail = await apiUtils.fetchData<ChatThreadDetail>(
      `${THREADS_ENDPOINT}/${encodeURIComponent(thread.id)}`,
    );
    if (isApiError(detail)) continue;
    const hit = detail.messages.find((m) => m.ingestion_run_id !== null);
    if (hit) return thread.id;
  }
  return null;
}
